import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { submitFeedback, getFeedback, updateFeedback } from "../../../core/api/feedbackAPI";
import useAuth from "../../../core/hooks/useAuth";

const FEEDBACK_QUERY_KEY = ["user-feedback"];

const useFeedback = () => {
    const queryClient = useQueryClient();
    const { isLoggedIn, accessToken } = useAuth();

    // ── Fetch existing feedback (auto-detect POST vs PUT mode) ──
    const {
        data: feedbackData,
        isLoading: isFetching,
        error: fetchError,
    } = useQuery({
        queryKey: [...FEEDBACK_QUERY_KEY, accessToken],
        queryFn: getFeedback,
        enabled: isLoggedIn && !!accessToken,
        staleTime: 1 * 24 * 60 * 60 * 1000,
        retry: 1,
    });

    // Extract the first result (handles different possible backend response structures)
    const existingFeedback =
        feedbackData?.results?.[0] ||
        feedbackData?.data?.[0] ||
        (Array.isArray(feedbackData?.data) ? feedbackData.data[0] : feedbackData?.data) ||
        null;

    const hasExistingFeedback = !!existingFeedback;

    // ── Submit new feedback (POST) ──
    const submitMutation = useMutation({
        mutationFn: submitFeedback,
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: FEEDBACK_QUERY_KEY });
            toast.success(result?.message || "Feedback submitted successfully!");
        },
        onError: (error) => {
            toast.error(error?.message || "Failed to submit feedback. Please try again.");
        },
    });

    // ── Update existing feedback (PUT) ──
    const updateMutation = useMutation({
        mutationFn: updateFeedback,
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: FEEDBACK_QUERY_KEY });
            toast.success(result?.message || "Feedback updated successfully!");
        },
        onError: (error) => {
            toast.error(error?.message || "Failed to update feedback. Please try again.");
        },
    });

    // ── Unified handler — auto-picks POST or PUT ──
    const handleSubmitFeedback = (data) => {
        if (hasExistingFeedback) {
            updateMutation.mutate(data);
        } else {
            submitMutation.mutate(data);
        }
    };

    return {
        // Data
        existingFeedback,
        hasExistingFeedback,

        // Fetch state
        isFetching,
        fetchError,

        // Submit/Update
        handleSubmitFeedback,
        isSubmitting: submitMutation.isPending || updateMutation.isPending,
        isSuccess: submitMutation.isSuccess || updateMutation.isSuccess,

        // Auth
        isLoggedIn,
    };
};

export default useFeedback;
